import { useState, useTransition } from "react";
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
  Heading,
  Modal,
  ModalOverlay,
} from "react-aria-components";
import type { InlineCreateResult } from "@shared/inlineCreate";
import { Button } from "./Button";
import buttonStyles from "./Button.module.css";
import { PlusIcon } from "./Icon";
import styles from "./InlineCreate.module.css";
import { useCodeFromLabel } from "./useCodeFromLabel";

// A row that already exists, so the dialog can say so before a near-twin is
// minted. Filtered in memory from the list the form already holds — the whole
// point is that it costs no query and appears while the label is being typed.
export type ExistingRow = { code: string; label: string | null };

type InlineCreateInput = {
  code: string;
  label: string;
  description?: string;
};

type InlineCreateProps<TChoice> = {
  triggerLabel: string;
  title: string;
  // Why this row type is heavier than it looks. Skills are the progression
  // axis, topics are the browse shelves, traits are offer gates — each one is
  // a structural row, and duplicates of it break something quietly.
  note: string;
  labelPlaceholder: string;
  codePlaceholder: string;
  // Set only where the record cannot exist without prose: a skill carries the
  // sentence that says what counts toward it, and the column is NOT NULL.
  descriptionLabel?: string;
  descriptionPlaceholder?: string;
  existing: readonly ExistingRow[];
  // The trait picker mints from its own search, so the query already typed is
  // the label nobody should have to type twice.
  initialLabel?: string;
  action: (input: InlineCreateInput) => Promise<InlineCreateResult<TChoice>>;
  onCreated: (choice: TChoice) => void;
};

const MAX_NEAR_MATCHES = 3;

// Substring either way, so "Read" finds "Reading" and "Reading" finds "Read" —
// the near-twin that matters is the one nobody notices in a long list.
const findNearMatches = (
  existing: readonly ExistingRow[],
  label: string,
): ExistingRow[] => {
  const needle = label.trim().toLowerCase();

  if (needle.length < 3) {
    return [];
  }

  return existing
    .filter((row) => {
      const name = (row.label ?? row.code).toLowerCase();

      return name.includes(needle) || needle.includes(name);
    })
    .slice(0, MAX_NEAR_MATCHES);
};

type InlineCreateBodyProps<TChoice> = InlineCreateProps<TChoice> & {
  close: () => void;
};

// Mounted by the dialog and unmounted with it, which is what resets the fields
// between openings — state kept in the trigger would survive a cancel and
// reappear as someone else's half-typed skill.
const InlineCreateBody = <TChoice,>({
  title,
  note,
  labelPlaceholder,
  codePlaceholder,
  descriptionLabel,
  descriptionPlaceholder,
  existing,
  initialLabel = "",
  action,
  onCreated,
  close,
}: InlineCreateBodyProps<TChoice>) => {
  const { label, code, setLabel, setCode } = useCodeFromLabel(initialLabel);
  const [description, setDescription] = useState("");
  const [fieldErrors, setFieldErrors] = useState<
    Record<string, string[] | undefined>
  >({});
  const [isPending, startTransition] = useTransition();

  const nearMatches = findNearMatches(existing, label);

  const create = () => {
    startTransition(async () => {
      const result = await action({
        code,
        label,
        ...(descriptionLabel === undefined ? {} : { description }),
      });

      if (!result.wasCreated) {
        setFieldErrors(result.fieldErrors);

        return;
      }

      onCreated(result.choice);
      close();
    });
  };

  // The template form is a <form> and this dialog renders inside it, so Enter
  // must never reach it: React events travel the component tree rather than
  // the DOM one, and a portal does not break that path.
  const submitOnEnter = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    create();
  };

  const renderErrors = (name: string) => {
    const errors = fieldErrors[name];

    if (errors === undefined || errors.length === 0) {
      return null;
    }

    return (
      <p className={styles.error} role="alert">
        {errors.join(" ")}
      </p>
    );
  };

  return (
    <>
      <Heading slot="title" className={styles.title}>
        {title}
      </Heading>
      <p className={styles.note}>{note}</p>

      <div className={styles.fields}>
        <label className={styles.field}>
          <span className={styles.label}>Label</span>
          <input
            className={styles.input}
            value={label}
            placeholder={labelPlaceholder}
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={submitOnEnter}
          />
          {renderErrors("label")}
        </label>

        {nearMatches.length === 0 ? null : (
          <output className={styles.matches}>
            Already in the catalog:{" "}
            {nearMatches
              .map((row) => `${row.label ?? row.code} (${row.code})`)
              .join(", ")}
            . Bind one of those instead unless this is genuinely a different
            thing.
          </output>
        )}

        <label className={styles.field}>
          <span className={styles.label}>Code</span>
          <input
            className={styles.input}
            value={code}
            placeholder={codePlaceholder}
            onChange={(event) => setCode(event.target.value)}
            onKeyDown={submitOnEnter}
          />
          {renderErrors("code")}
        </label>

        {descriptionLabel === undefined ? null : (
          <label className={styles.field}>
            <span className={styles.label}>{descriptionLabel}</span>
            <textarea
              className={styles.textarea}
              value={description}
              placeholder={descriptionPlaceholder}
              rows={2}
              onChange={(event) => setDescription(event.target.value)}
            />
            {renderErrors("description")}
          </label>
        )}
      </div>

      <div className={styles.actions}>
        {/* type="button" is load-bearing for the same reason Enter is trapped
            above: a submit button here would post the template. */}
        <Button
          type="button"
          variant="primary"
          disabled={isPending}
          onClick={create}
        >
          {isPending ? "Creating..." : "Create"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={isPending}
          onClick={close}
        >
          Cancel
        </Button>
      </div>
    </>
  );
};

export const InlineCreate = <TChoice,>(props: InlineCreateProps<TChoice>) => (
  <DialogTrigger>
    <AriaButton className={buttonStyles.button} data-variant="ghost">
      <PlusIcon size={13} />
      {props.triggerLabel}
    </AriaButton>
    <ModalOverlay className={styles.overlay} isDismissable>
      <Modal className={styles.modal}>
        <Dialog className={styles.dialog}>
          {({ close }) => <InlineCreateBody {...props} close={close} />}
        </Dialog>
      </Modal>
    </ModalOverlay>
  </DialogTrigger>
);
